import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  BoxExtendedProps,
  FloatImage,
  Image,
  NavigationLeftIcon,
  Text,
  useResponsive,
} from '@common-fe/common-fe';

import LoginBackgroundImage from '@/assets/backgrounds/loginBackground.svg';
import logoImage from '@/assets/login-logo.svg';
import { EMPTY_WHITE_LABEL_STRING } from '@/common/constants';
import { VisualImpairment } from '@/modules/core/components';
import { SignInContainer, SignInTitleContainer } from '@/modules/user/components/SignIn/SignInWrapper.styles';
import { whiteLabelingConfig } from '@/utils/whiteLabeling';
import { THEMES } from '@/utils/whiteLabeling/whiteLabelingConfig';

import {
  BackButton,
  BackgroundWrapper,
  Footer,
  FormContent,
  FormSubtitle,
  FormTitle,
  FormWrapper,
  RightBlockWrapper,
  Wrapper,
} from './SignInWrapper.styles';

const isRmrTheme = whiteLabelingConfig.key === THEMES.rmr;
const isGustoTheme = whiteLabelingConfig.key === THEMES.gusto;
const isRipplingTheme = whiteLabelingConfig.key === THEMES.rippling;
const logoWidth = isRmrTheme ? 330 : 240;
const logoHeight = isRmrTheme ? 159 : 65;

interface Props {
  children?: React.ReactNode;
  title?: string | React.ReactNode;
  subtitle?: string;
  isBackButton?: boolean;
  onBack?: () => void;
  flat?: boolean;
  header?: React.ReactNode;
  isTitleLess?: boolean;
  formWrapperProps?: BoxExtendedProps;
}

const SignIn: React.FC<Props> = ({
  flat,
  children,
  title,
  subtitle,
  isBackButton,
  header,
  onBack,
  isTitleLess,
  formWrapperProps,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();

  const isPartnerLogin = useMemo(() => isRipplingTheme || isGustoTheme, []);
  return (
    <Wrapper>
      <BackgroundWrapper>
        <FloatImage
          src={whiteLabelingConfig?.images?.loginBackground || LoginBackgroundImage}
          fit="cover"
          style={{
            backgroundPosition: 'center bottom',
            bottom: 0,
            left: 0,
            right: 0,
          }}
        />
      </BackgroundWrapper>
      <SignInContainer
        direction="row"
        style={{
          zIndex: 1,
          marginTop: isPartnerLogin || isMobile ? undefined : '100px',
        }}
      >
        <SignInTitleContainer
          flex
          {...(isPartnerLogin ? { justify: 'center' } : { margin: { top: '80px' } })}
        >
          <Image
            src={whiteLabelingConfig?.images?.signInLogo || whiteLabelingConfig?.logo || logoImage}
            width={whiteLabelingConfig?.loginIconWidth || logoWidth}
            alt="logo"
            height={whiteLabelingConfig?.loginIconHeight || logoHeight}
          />

          {!isTitleLess && whiteLabelingConfig?.loginScreenTitle !== EMPTY_WHITE_LABEL_STRING && (
            <Text
              size={isMobile ? 'small' : 'xxlarge'}
              margin={isMobile ? { top: 'spacing12', horizontal: 'spacing12' } : { top: 'spacing24' }}
              color="loginTitleColor"
              textAlign={isMobile ? 'center' : 'start'}
              weight={isMobile ? 500 : 400}
              style={{
                maxWidth: isRmrTheme ? '400px' : '600px',
              }}
            >
              {t(
                whiteLabelingConfig?.loginScreenTitle ||
                  t(
                    'The only automated, AI-powered, and ultra-reliable solution for today’s consumer benefits.'
                  )
              )}
            </Text>
          )}
        </SignInTitleContainer>
        <Box direction="column" {...formWrapperProps}>
          <RightBlockWrapper>
            <FormWrapper flat={flat} isBackButton={isBackButton}>
              {isBackButton && (
                <BackButton isMobileView={isMobile} onClick={onBack} type="button">
                  <NavigationLeftIcon size="24px" color="iconPrimary" />
                </BackButton>
              )}
              {header}
              {typeof title === 'string' ? <FormTitle>{title}</FormTitle> : title}
              {subtitle && <FormSubtitle isPartnerLogin={isPartnerLogin}>{subtitle}</FormSubtitle>}
              <FormContent>{children}</FormContent>
            </FormWrapper>
          </RightBlockWrapper>
          {whiteLabelingConfig?.isLoginFooter && (
            <Box align="center" pad={{ top: 'm' }}>
              <Text color="textOnColor" weight={400}>
                Powered by Elevate
              </Text>
            </Box>
          )}
        </Box>
      </SignInContainer>

      {isPartnerLogin ? null : (
        <Footer style={{ visibility: 'hidden' }}>
          <VisualImpairment />
        </Footer>
      )}
    </Wrapper>
  );
};

export default SignIn;
